/**
 * Leaflet layer drawn into a named pane.
 * Structural type: this app talks to the map through `window.L`, not a Leaflet import.
 */
interface PaneLayer {
  options?: {pane?: string};
  getElement?: () => HTMLElement | undefined;
  getContainer?: () => HTMLElement | undefined;
}

interface PaneMap {
  eachLayer?: (fn: (layer: PaneLayer) => void) => void;
  removeLayer?: (layer: PaneLayer) => void;
  hasLayer?: (layer: PaneLayer) => boolean;
  getPane?: (name: string) => HTMLElement | undefined;
}

/**
 * Removes every Leaflet layer drawn into `paneName`, then drops leftover DOM
 * that no remaining layer still owns (canvas tiles whose GridLayer handle was already lost).
 * Nodes that a live layer still references stay on the pane, so Leaflet does not
 * keep a layer whose container was deleted out from under it.
 */
export function clearLeafletDataPane(
  map: PaneMap | null | undefined,
  paneName: string | null | undefined,
): void {
  if (!map || !paneName) {
    return;
  }
  const doomed: PaneLayer[] = [];
  map.eachLayer?.((layer) => {
    if (layer?.options?.pane === paneName) {
      doomed.push(layer);
    }
  });
  for (const layer of doomed) {
    if (map.hasLayer && !map.hasLayer(layer)) {
      continue;
    }
    map.removeLayer?.(layer);
  }
  const pane = map.getPane?.(paneName);
  if (!pane) {
    return;
  }
  const owned = layerElements(map);
  let child: ChildNode | null = pane.firstChild;
  while (child) {
    const next = child.nextSibling;
    if (!nodeBelongsToLayer(child, owned)) {
      pane.removeChild(child);
    }
    child = next;
  }
}

function layerElements(map: PaneMap): Set<Node> {
  const owned = new Set<Node>();
  map.eachLayer?.((layer) => {
    const element = layer.getElement?.() ?? layer.getContainer?.();
    if (element) {
      owned.add(element);
    }
  });
  return owned;
}

function nodeBelongsToLayer(node: Node, owned: Set<Node>): boolean {
  for (const element of owned) {
    if (node === element || node.contains(element) || element.contains(node)) {
      return true;
    }
  }
  return false;
}
